/**
 * AssetResolver — single entry point for resolving logical asset IDs to
 * absolute CDN URLs. Backed by the manifest served by corpllm-server's
 * GET /api/assets/manifest endpoint.
 *
 * Architecture: Weg B (hash-CDN + authed manifest).
 *   1. Boot fetches the manifest once (authed).
 *   2. Manifest contains { cdnBase, assets: { id: { r2Key, ... } } }.
 *   3. resolve(id) returns `${cdnBase}/${r2Key}` — no per-file roundtrip.
 *
 * The corpllm-server endpoint signs the manifest under the hood; the actual
 * asset bytes come straight from the public Cloudflare R2 custom domain
 * (assets.corpllm.io). Filenames are content-addressed via short sha256, so
 * leaked URLs are stable across builds but rotate when content changes.
 *
 * See docs/asset-pipeline.md in corpllm-assets for the full architecture.
 */

import { fetchAPI } from '../api';

// ── Manifest types (must match corpllm-assets/tools/build-manifest.mjs) ─────

export interface AssetEntry {
  type: string;       // 'glb' | 'webp' | 'png'
  r2Key: string;      // 'staging/attachments/backpack_01.abe71d337624.glb'
  sha256: string;     // full hash, NOT verified client-side
  size: number;       // bytes
}

export interface AssetManifest {
  version: number;            // unix seconds, monotonically increasing
  env: 'prod' | 'staging';
  cdnBase: string;            // 'https://assets.corpllm.io' (no trailing slash)
  builtAt: string;            // ISO timestamp
  assetCount: number;
  totalBytes: number;
  assets: Record<string, AssetEntry>;
}

export type AssetEnv = 'prod' | 'staging';

// ── Singleton resolver ──────────────────────────────────────────────────────

let _manifest: AssetManifest | null = null;
let _initPromise: Promise<void> | null = null;

/**
 * Fetch the asset manifest from corpllm-server. Idempotent — repeated calls
 * with the same env return the existing in-flight or completed initialization.
 *
 * Throws if the fetch fails or the user is not authenticated. The caller is
 * expected to surface a hard error screen in that case (no silent fallback).
 */
export function initAssetResolver(env: AssetEnv = 'prod'): Promise<void> {
  if (_initPromise) return _initPromise;
  _initPromise = (async () => {
    const manifest = await fetchAPI<AssetManifest>(`/api/assets/manifest?env=${env}`);
    if (!manifest || typeof manifest !== 'object' || !manifest.cdnBase || !manifest.assets) {
      throw new Error('AssetResolver: malformed manifest from server');
    }
    if (manifest.cdnBase.endsWith('/')) {
      // Defensive: build-manifest already strips this, but trust nothing.
      manifest.cdnBase = manifest.cdnBase.replace(/\/+$/, '');
    }
    _manifest = manifest;
    console.log(
      `[AssetResolver] manifest loaded: env=${manifest.env} v=${manifest.version} ` +
      `${manifest.assetCount} assets ${(manifest.totalBytes / 1e6).toFixed(1)}MB`
    );
  })();
  return _initPromise;
}

/**
 * Resolve a logical asset ID to its absolute CDN URL.
 *
 * Logical IDs follow the path under assets/output/ without the extension:
 *   'attachments/backpack_01'
 *   'characters/cybercity/runner_01'
 *   'props/hologram_table_02'
 *
 * Throws if the resolver was not initialized or the ID is unknown — both
 * indicate a hard bug, not a runtime fallback condition.
 */
export function resolveAsset(id: string): string {
  if (!_manifest) {
    throw new Error(`AssetResolver: not initialized (cannot resolve "${id}")`);
  }
  const entry = _manifest.assets[id];
  if (!entry) {
    throw new Error(`AssetResolver: unknown asset id "${id}"`);
  }
  return `${_manifest.cdnBase}/${entry.r2Key}`;
}

/** Soft variant — returns null instead of throwing for unknown IDs. */
export function tryResolveAsset(id: string): string | null {
  if (!_manifest) return null;
  const entry = _manifest.assets[id];
  if (!entry) return null;
  return `${_manifest.cdnBase}/${entry.r2Key}`;
}

/** Returns the loaded manifest, or null if init() has not run yet. */
export function getAssetManifest(): AssetManifest | null {
  return _manifest;
}

/** Test/dev hook — clears the cached manifest so init() runs again. */
export function _resetAssetResolverForTests(): void {
  _manifest = null;
  _initPromise = null;
}

/** Test/dev hook — inject a manifest directly (no backend fetch). Used by the
 *  render harness with a Proxy-backed `assets` so resolveAsset never throws. */
export function _setManifestForDev(m: AssetManifest): void {
  _manifest = m;
  _initPromise = Promise.resolve();
}
